import { useEffect, useState } from "react"
import { useToast, ToastMessage } from "@stayframe/ui"
import { formatApiError } from "@/lib/api"
import { opsAdminApi } from "@/lib/opsAdminApi"

interface SubscriptionRow {
  property_id: string
  property_name: string
  property_slug: string
  legacy_tier: string
  owner_email: string
  owner_name: string
  plan: string
  plan_status: string
  is_trialing: boolean
  trial_ends_at: string | null
  degraded: boolean
  subscription_id: string | null
  created_at: string
}

const PLAN_OPTIONS = ["host", "insight", "concierge"] as const
type PlanOption = (typeof PLAN_OPTIONS)[number]

const PLAN_COLORS: Record<string, string> = {
  host: "var(--sf-text-secondary)",
  insight: "var(--sf-accent-blue, #3b82f6)",
  concierge: "var(--sf-accent-purple, #8b5cf6)",
}

const STATUS_COLORS: Record<string, string> = {
  trialing: "var(--sf-accent-blue, #3b82f6)",
  active: "var(--sf-accent-green, #22c55e)",
  past_due: "var(--sf-accent-orange, #f59e0b)",
  canceled: "var(--sf-text-tertiary)",
  expired: "var(--sf-text-tertiary)",
  trial_expired: "var(--sf-accent-red, #ef4444)",
  no_subscription: "var(--sf-text-tertiary)",
}

function formatTrialRemaining(trialEndsAt: string | null): string {
  if (!trialEndsAt) return ""
  const end = new Date(trialEndsAt)
  const now = new Date()
  const diffMs = end.getTime() - now.getTime()
  if (diffMs <= 0) return "expired"
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  return `${days}d left`
}

export function OpsSubscriptionsCard() {
  const { toast } = useToast()
  const [rows, setRows] = useState<SubscriptionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [changingPlan, setChangingPlan] = useState<string | null>(null)
  const [actionBusy, setActionBusy] = useState("")

  async function loadSubscriptions() {
    setLoading(true)
    const res = await opsAdminApi.getSubscriptions()
    if (res.ok && res.subscriptions) {
      setRows(res.subscriptions)
    } else {
      const error = formatApiError(res as unknown as Record<string, unknown>)
      toast(
        <ToastMessage
          message={error.message || "Failed to load subscriptions."}
          requestId={error.requestId}
        />,
        "error",
      )
    }
    setLoading(false)
  }

  useEffect(() => {
    loadSubscriptions()
  }, [])

  async function handleSetPlan(propertyId: string, plan: PlanOption) {
    setActionBusy(propertyId)
    const res = await opsAdminApi.runPropertyAction(propertyId, "set_plan", { plan })
    if (res.ok) {
      toast(`Plan set to ${plan} for ${propertyId.slice(0, 8)}…`, "success")
      await loadSubscriptions()
    } else {
      const error = formatApiError(res as unknown as Record<string, unknown>)
      toast(
        <ToastMessage
          message={error.message || "Failed to set plan."}
          requestId={error.requestId}
        />,
        "error",
      )
    }
    setActionBusy("")
    setChangingPlan(null)
  }

  async function handleStartTrial(propertyId: string) {
    setActionBusy(propertyId)
    const res = await opsAdminApi.runPropertyAction(propertyId, "start_trial")
    if (res.ok) {
      toast(`Trial started for ${propertyId.slice(0, 8)}…`, "success")
      await loadSubscriptions()
    } else {
      const error = formatApiError(res as unknown as Record<string, unknown>)
      toast(
        <ToastMessage
          message={error.message || "Failed to start trial."}
          requestId={error.requestId}
        />,
        "error",
      )
    }
    setActionBusy("")
  }

  async function handleExpireTrial(propertyId: string) {
    setActionBusy(propertyId)
    const res = await opsAdminApi.runPropertyAction(propertyId, "expire_trial")
    if (res.ok) {
      toast(`Trial expired for ${propertyId.slice(0, 8)}…`, "success")
      await loadSubscriptions()
    } else {
      const error = formatApiError(res as unknown as Record<string, unknown>)
      toast(
        <ToastMessage
          message={error.message || "Failed to expire trial."}
          requestId={error.requestId}
        />,
        "error",
      )
    }
    setActionBusy("")
  }

  const filtered = search.trim()
    ? rows.filter(
        (r) =>
          r.property_name.toLowerCase().includes(search.toLowerCase()) ||
          r.owner_email.toLowerCase().includes(search.toLowerCase()) ||
          r.owner_name.toLowerCase().includes(search.toLowerCase()) ||
          r.property_id.toLowerCase().includes(search.toLowerCase()),
      )
    : rows

  if (loading) {
    return (
      <div className="section-card">
        <div className="section-card__title">Subscription & Tier Testing</div>
        <div className="ov-skeleton ov-skeleton--text" style={{ width: "60%", height: 16 }} />
      </div>
    )
  }

  return (
    <div className="section-card">
      <div className="section-card__header-row" style={{ marginBottom: 12 }}>
        <div className="section-card__title" style={{ marginBottom: 0 }}>
          Subscription & Tier Testing
        </div>
        <span
          className="ops-badge"
          style={{
            marginLeft: 8,
            background: "var(--sf-accent-purple, #8b5cf6)",
            color: "#fff",
          }}
        >
          Temp
        </span>
      </div>
      <p
        style={{
          fontSize: "var(--sf-text-sm)",
          color: "var(--sf-text-secondary)",
          marginBottom: 12,
        }}
      >
        Manually set subscription tiers for testing. This card will be removed
        when Stripe billing is integrated.
      </p>

      <div style={{ marginBottom: 12 }}>
        <input
          type="text"
          placeholder="Search by name, email, or property ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="ops-intel-gate-field__input"
          style={{ width: "100%", maxWidth: 360 }}
        />
      </div>

      <div style={{ overflowX: "auto" }}>
        <table
          className="data-table"
          style={{ width: "100%", fontSize: "var(--sf-text-sm)" }}
        >
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>Property</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>Owner</th>
              <th style={{ textAlign: "center", padding: "6px 8px" }}>Plan</th>
              <th style={{ textAlign: "center", padding: "6px 8px" }}>Status</th>
              <th style={{ textAlign: "center", padding: "6px 8px" }}>Trial</th>
              <th style={{ textAlign: "center", padding: "6px 8px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  style={{
                    textAlign: "center",
                    padding: 16,
                    color: "var(--sf-text-tertiary)",
                  }}
                >
                  {search.trim()
                    ? "No matching properties."
                    : "No properties found."}
                </td>
              </tr>
            )}
            {filtered.map((row) => {
              const isBusy = actionBusy === row.property_id
              const isChanging = changingPlan === row.property_id
              return (
                <tr key={row.property_id}>
                  <td style={{ padding: "6px 8px" }}>
                    <div style={{ fontWeight: 500 }}>{row.property_name || "—"}</div>
                    <div
                      style={{
                        fontSize: "var(--sf-text-xs)",
                        color: "var(--sf-text-tertiary)",
                        fontFamily: "var(--sf-font-mono)",
                      }}
                    >
                      {row.property_id.slice(0, 12)}…
                    </div>
                  </td>
                  <td style={{ padding: "6px 8px" }}>
                    <div>{row.owner_name || "—"}</div>
                    <div
                      style={{
                        fontSize: "var(--sf-text-xs)",
                        color: "var(--sf-text-tertiary)",
                      }}
                    >
                      {row.owner_email}
                    </div>
                  </td>
                  <td style={{ textAlign: "center", padding: "6px 8px" }}>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: 4,
                        fontSize: "var(--sf-text-xs)",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "#fff",
                        background: PLAN_COLORS[row.plan] || "var(--sf-text-tertiary)",
                      }}
                    >
                      {row.plan}
                    </span>
                  </td>
                  <td style={{ textAlign: "center", padding: "6px 8px" }}>
                    <span
                      style={{
                        fontSize: "var(--sf-text-xs)",
                        color:
                          STATUS_COLORS[row.plan_status] ||
                          "var(--sf-text-secondary)",
                      }}
                    >
                      {row.plan_status}
                    </span>
                  </td>
                  <td style={{ textAlign: "center", padding: "6px 8px" }}>
                    {row.is_trialing && row.trial_ends_at ? (
                      <span
                        style={{
                          fontSize: "var(--sf-text-xs)",
                          color: "var(--sf-accent-blue, #3b82f6)",
                        }}
                      >
                        {formatTrialRemaining(row.trial_ends_at)}
                      </span>
                    ) : (
                      <span
                        style={{
                          fontSize: "var(--sf-text-xs)",
                          color: "var(--sf-text-tertiary)",
                        }}
                      >
                        —
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: "center", padding: "6px 8px" }}>
                    {isChanging ? (
                      <div
                        style={{
                          display: "flex",
                          gap: 4,
                          justifyContent: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        {PLAN_OPTIONS.map((p) => (
                          <button
                            key={p}
                            type="button"
                            className="data-table__action"
                            disabled={isBusy}
                            onClick={() => handleSetPlan(row.property_id, p)}
                            style={{
                              fontSize: "var(--sf-text-xs)",
                              padding: "2px 6px",
                              background:
                                row.plan === p
                                  ? PLAN_COLORS[p]
                                  : "transparent",
                              color:
                                row.plan === p
                                  ? "#fff"
                                  : PLAN_COLORS[p],
                              border: `1px solid ${PLAN_COLORS[p]}`,
                              borderRadius: 3,
                              cursor: isBusy ? "wait" : "pointer",
                              textTransform: "uppercase",
                              fontWeight: 600,
                              letterSpacing: "0.3px",
                            }}
                          >
                            {p}
                          </button>
                        ))}
                        <button
                          type="button"
                          className="data-table__action"
                          onClick={() => setChangingPlan(null)}
                          style={{
                            fontSize: "var(--sf-text-xs)",
                            padding: "2px 6px",
                            color: "var(--sf-text-tertiary)",
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          gap: 4,
                          justifyContent: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          type="button"
                          className="data-table__action"
                          disabled={isBusy}
                          onClick={() => setChangingPlan(row.property_id)}
                          style={{
                            fontSize: "var(--sf-text-xs)",
                            padding: "2px 6px",
                          }}
                        >
                          Set Tier
                        </button>
                        {!row.subscription_id && (
                          <button
                            type="button"
                            className="data-table__action"
                            disabled={isBusy}
                            onClick={() =>
                              handleStartTrial(row.property_id)
                            }
                            style={{
                              fontSize: "var(--sf-text-xs)",
                              padding: "2px 6px",
                              color: "var(--sf-accent-blue, #3b82f6)",
                            }}
                          >
                            Start Trial
                          </button>
                        )}
                        {row.is_trialing && (
                          <button
                            type="button"
                            className="data-table__action"
                            disabled={isBusy}
                            onClick={() =>
                              handleExpireTrial(row.property_id)
                            }
                            style={{
                              fontSize: "var(--sf-text-xs)",
                              padding: "2px 6px",
                              color: "var(--sf-accent-red, #ef4444)",
                            }}
                          >
                            Expire Trial
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div
        style={{
          marginTop: 8,
          fontSize: "var(--sf-text-xs)",
          color: "var(--sf-text-tertiary)",
        }}
      >
        {filtered.length} of {rows.length} properties shown
      </div>
    </div>
  )
}
